/**
 * Clearer items (accessibility, roadmap-3k §11) view helpers, free of Phaser so they are unit-tested
 * in node. From arc III (city order 14 on) generation picks some clutter types for the likeness of
 * their silhouette to the order's items (lookalike clutter, lever 2), and the level file's
 * `lookalikes` maps each of them to a substitute: an item of another shop of the same city whose
 * silhouette is least like it, which the level does not hold (so the substitute's art never repeats
 * one already on screen) and which no other lookalike of the level uses. Its art ships in the same
 * `items_<city>` group as every item of the city, with its colour-blind variant.
 *
 * With the setting on (settings.clearerItems, state.ts clearerItemsOn) a piece of a lookalike item
 * is DRAWN with its substitute's art, on the board, the belt, a machine, a tray, in the bag and in
 * the long-press preview, and in the replay clip. That is all it is: presentation. The engine's item
 * ids, events, test hooks that report item indices and the round's input log never see it (`lookalikes`
 * is not in the level hash), so a replay of a round played with it on is the round played with it
 * off. Which art draws an item id is read once per level here, not per piece, and the colour-blind
 * variant is the art library's (`Art.item`), asked for the substitute like for any item.
 *
 * Ticket items are never lookalikes (core: a lookalike is a clutter item), so the ticket, the serve
 * flights and the order icons in the clip keep drawing the item itself.
 */
import type { CompiledLevel } from '@mp/core';

export interface ItemLooks {
  /** is any item of the level drawn as another one (the setting is on and the level has lookalikes)? */
  readonly active: boolean;
  /** the item whose art draws item `id`: its substitute when it is a lookalike and the setting is on, else `id` itself */
  of(id: string): string;
}

/**
 * The asset an image shows, for the test hooks: its atlas frame's id (the frame's name without the
 * file extension, as the art library keys it), or the id the procedural stand-in of a missing frame
 * was made for (`real` false). Takes anything shaped like a Phaser image, so it needs no Phaser.
 */
export function drawnArt(img: { texture: { key: string }; frame: { name: string } }): { id: string; real: boolean } {
  const key = img.texture.key;
  if (key.startsWith('fb:')) return { id: key.slice(3), real: false };
  return { id: img.frame.name.replace(/\.(png|webp|jpg)$/i, ''), real: true };
}

/** Every item drawn as itself: the setting off, or a level without lookalikes. */
export const NO_LOOKS: ItemLooks = { active: false, of: (id) => id };

/** The looks of a level's items under the setting (`on`: clearerItemsOn of the player's settings). */
export function itemLooks(L: CompiledLevel, on: boolean): ItemLooks {
  const given = L.level.lookalikes;
  if (!on || !given) return NO_LOOKS;
  // only items the level holds (a piece is never anything else); core's validateLevel keeps the rest honest
  const map = new Map(Object.entries(given).filter(([item]) => L.itemIndex.has(item)));
  if (!map.size) return NO_LOOKS;
  return { active: true, of: (id) => map.get(id) ?? id };
}
